"use client";

import {
  IconArrowSquareOut,
  IconCheckCircleFill,
  IconHourglassHigh,
  IconWarningCircleFill,
} from "@decibeltrade/react-icons";
import type { ReactNode } from "react";
import { css, cva } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

export type StepIconVariant = "complete" | "inProgress" | "cancelled" | "pending";

export function StepIcon({ variant }: { variant: StepIconVariant }) {
  switch (variant) {
    case "complete":
      return <IconCheckCircleFill className={stepIconStyles({ variant })} />;
    case "inProgress":
      return <IconHourglassHigh className={stepIconStyles({ variant })} />;
    case "cancelled":
      return <IconWarningCircleFill className={stepIconStyles({ variant })} />;
    case "pending":
      return <div className={stepIconStyles({ variant })} />;
  }
}

export function TxLink({
  url,
  ...rest
}: {
  url: string | null;
  "data-testid"?: string;
  "data-link"?: string;
}) {
  if (!url) return null;
  return (
    <>
      {" "}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={explorerLinkStyles}
        {...rest}
      >
        <IconArrowSquareOut className={css({ w: "12", h: "12", flexShrink: "0" })} />
      </a>
    </>
  );
}

export function StepRow({
  icon,
  connector,
  title,
  subtitle,
  muted,
  trailing,
  ...rest
}: {
  icon: ReactNode;
  connector: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  muted: boolean;
  trailing?: ReactNode;
  "data-testid": string;
  "data-step-status": string;
}) {
  return (
    <div {...rest}>
      <div className={flex({ align: "flex-start", gap: "12" })}>
        <div className={flex({ direction: "column", align: "center" })}>
          {icon}
          {connector && <div className={progressLineStyles} />}
        </div>

        <div
          className={flex({
            align: "flex-end",
            justify: "space-between",
            w: "full",
            textAlign: "left",
          })}
        >
          <div className={stack({ gap: "4", flex: "1" })}>
            <p
              className={flex({
                align: "center",
                gap: "4",
                textStyle: "body.md",
                color: muted ? "text.tertiary" : "text.primary",
              })}
            >
              {title}
            </p>
            {subtitle}
          </div>
          {trailing}
        </div>
      </div>
    </div>
  );
}

export const transferCardStyles = cva({
  base: {
    p: "12",
    display: "flex",
    flexDirection: "column",
    gap: "12",
    bg: "card.background",
    borderWidth: "100",
    borderRadius: "200",
    borderColor: "border.primary",
    transitionPreset: "colors",
  },
  variants: {
    highlighted: {
      true: {
        bg: "background.tertiary",
        borderColor: "brandYellow.200",
      },
    },
  },
});

export const stepIconStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    w: "16",
    h: "16",
    borderRadius: "full",
    flexShrink: 0,
  },
  variants: {
    variant: {
      complete: {
        color: "icon.positive",
      },
      inProgress: {
        color: "icon.tertiary",
      },
      cancelled: {
        color: "icon.negative",
      },
      pending: {
        w: "14",
        h: "14",
        mx: "1",
        border: "1px solid",
        borderColor: "border.secondary",
      },
    },
  },
});

export const stepSubtitleStyles = css({ textStyle: "body.sm", color: "text.tertiary" });

export const progressContainerStyles = stack({
  gap: "0",
});

const progressLineStyles = css({
  w: "1",
  h: "24",
  bg: "border.secondary",
  my: "4",
});

export const explorerLinkStyles = css({
  color: "text.secondary",
  textDecoration: "underline",
  textDecorationSkipInk: "none",
  textUnderlinePosition: "from-font",
  _hover: { color: "text.primary" },
});
